import { useEffect, useState } from "react";
import { toast } from "../../../utils/notify";
import {
  ShieldCheckIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  CheckCircleIcon,
  ArrowUpTrayIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import ApiLoader from "../../ui/ApiLoader";
import {
  fetchCustomerComplianceFrameworks,
  fetchCustomerComplianceFrameworkControls,
  uploadCustomerComplianceControlDocument,
} from "../../../services/axios/AuthApis/DashboardApis";
import { DataTable } from "../../common/DataTable";

// ================= DSPM Compliance (CustomerAdmin) =================
// Read-only list of the frameworks already assigned/enabled for this
// customer (DSPMCustomerComplianceFramework) - no self-service
// enable/disable here; enablement is managed elsewhere.
const STATUS_BADGE = {
  compliant: "bg-green-100 text-green-700",
  partial: "bg-yellow-100 text-yellow-700",
  failed: "bg-red-100 text-red-700",
  not_evaluated: "bg-gray-100 text-gray-500",
};

const STATUS_LABEL = {
  compliant: "Compliant",
  partial: "Partial",
  failed: "Failed",
  not_evaluated: "Not Evaluated",
};

const Compliance = () => {
  const [frameworks, setFrameworks] = useState([]);
  const [fwLoading, setFwLoading] = useState(true);

  const [selectedFw, setSelectedFw] = useState(null);
  const [controls, setControls] = useState([]);
  const [ctrlLoading, setCtrlLoading] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);

  const loadFrameworks = async () => {
    setFwLoading(true);
    try {
      const d = await fetchCustomerComplianceFrameworks();
      setFrameworks(Array.isArray(d?.frameworks) ? d.frameworks : []);
    } catch {
      toast.error("Failed to load compliance frameworks");
    } finally {
      setFwLoading(false);
    }
  };

  useEffect(() => {
    loadFrameworks();
  }, []);

  const openFramework = async (fw) => {
    setSelectedFw(fw);
    setControls([]);
    setCtrlLoading(true);
    try {
      const d = await fetchCustomerComplianceFrameworkControls(fw.id);
      setControls(Array.isArray(d?.controls) ? d.controls : []);
    } catch {
      toast.error("Failed to load controls");
    } finally {
      setCtrlLoading(false);
    }
  };

  const goBack = () => {
    setSelectedFw(null);
    setControls([]);
  };

  const handleUploadDocument = async (control, file) => {
    if (!file) return;
    setUploadingId(control.id);
    try {
      const res = await uploadCustomerComplianceControlDocument(control.id, file);
      toast.success(`Uploaded to Trust Center under ${res.framework}`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to upload document");
    } finally {
      setUploadingId(null);
    }
  };

  if (selectedFw) {
    const uniqueCategories = [...new Set(controls.map((c) => c.category).filter(Boolean))];

    const controlColumns = [
      {
        id: "control_id",
        accessorFn: (c) => c.control_id,
        header: "Control ID",
        enableSorting: false,
        meta: { filter: { variant: "text", label: "Control ID", placeholder: "Search control ID" } },
        cell: ({ row }) => <span className="font-mono text-xs text-gray-700">{row.original.control_id}</span>,
      },
      {
        id: "title",
        accessorFn: (c) => c.title,
        header: "Control",
        enableSorting: false,
        meta: { filter: { variant: "text", label: "Control", placeholder: "Search control" } },
        cell: ({ row }) => (
          <div>
            <p className="font-medium text-gray-900">{row.original.title}</p>
            {row.original.description && (
              <p className="text-xs text-gray-500 mt-0.5">{row.original.description}</p>
            )}
          </div>
        ),
      },
      {
        id: "category",
        accessorFn: (c) => c.category,
        header: "Category",
        enableSorting: false,
        meta: {
          filter: {
            variant: "select",
            label: "Category",
            placeholder: "All categories",
            options: uniqueCategories.map((v) => ({ value: v, label: v })),
          },
        },
        cell: ({ row }) => (
          <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-primary-50 text-primary-700">
            {row.original.category || "-"}
          </span>
        ),
      },
      {
        id: "status",
        accessorFn: (c) => c.status,
        header: "Status",
        enableSorting: false,
        meta: {
          filter: {
            variant: "select",
            label: "Status",
            placeholder: "All statuses",
            options: [
              { value: "passed", label: "Passed" },
              { value: "failed", label: "Failed" },
              { value: "not_evaluated", label: "Not Evaluated" },
            ],
          },
        },
        cell: ({ row }) => {
          const status = row.original.status;
          return (
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                status === "passed" ? "bg-green-100 text-green-700"
                  : status === "failed" ? "bg-red-100 text-red-700"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {status === "passed" ? "Passed" : status === "failed" ? "Failed" : "Not Evaluated"}
            </span>
          );
        },
      },
      {
        id: "evidence",
        header: "Evidence",
        enableSorting: false,
        cell: ({ row }) => {
          const c = row.original;
          return (
            <label
              className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-gray-300 rounded-lg text-gray-600 hover:text-primary-600 hover:border-primary-300 transition-colors cursor-pointer ${
                uploadingId === c.id ? "opacity-50 pointer-events-none" : ""
              }`}
            >
              <ArrowUpTrayIcon className="w-3.5 h-3.5" />
              {uploadingId === c.id ? "Uploading..." : "Upload Evidence"}
              <input
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  handleUploadDocument(c, file);
                }}
              />
            </label>
          );
        },
      },
    ];

    return (
      <div>
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-primary-600 mb-4"
        >
          <ChevronLeftIcon className="w-4 h-4" />
          Back to Frameworks
        </button>

        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {selectedFw.name}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Controls assigned for your account
            </p>
          </div>
          <span className="text-sm text-gray-500">
            {controls.length} controls
          </span>
        </div>

        <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
          <DataTable
            columns={controlColumns}
            data={controls}
            getRowId={(c) => String(c.id)}
            loading={ctrlLoading}
            emptyMessage="No controls found"
            enableColumnVisibility={false}
            enableColumnResizing
            enableColumnFilters
            maxBodyHeight="65vh"
          />
        </div>
      </div>
    );
  }

  const uniqueStatuses = [...new Set(frameworks.map((f) => f.status).filter(Boolean))];

  const frameworkColumns = [
    {
      id: "name",
      accessorFn: (fw) => fw.name,
      header: "Framework",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Framework", placeholder: "Search framework" } },
      cell: ({ row }) => (
        <div>
          <p className="font-medium text-gray-900">{row.original.name}</p>
          <p className="text-xs text-gray-500">{row.original.controls_count} controls</p>
        </div>
      ),
    },
    {
      id: "status",
      accessorFn: (fw) => fw.status,
      header: "Status",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          placeholder: "All statuses",
          options: uniqueStatuses.map((v) => ({ value: v, label: STATUS_LABEL[v] || v })),
        },
      },
      cell: ({ row }) => {
        const fw = row.original;
        return (
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs ${STATUS_BADGE[fw.status] || STATUS_BADGE.not_evaluated}`}>
            {fw.status === "compliant" && <CheckCircleIcon className="w-3.5 h-3.5" />}
            {STATUS_LABEL[fw.status] || "Not Evaluated"}
          </span>
        );
      },
    },
    {
      id: "score",
      accessorFn: (fw) => fw.score,
      header: "Score",
      enableSorting: false,
      cell: ({ row }) => (
        <span className="font-semibold text-gray-900">
          {row.original.score === null || row.original.score === undefined ? "-" : `${row.original.score}%`}
        </span>
      ),
    },
    {
      id: "last_sync",
      accessorFn: (fw) => fw.last_sync,
      header: "Last Sync",
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-gray-600">
          {row.original.last_sync ? new Date(row.original.last_sync).toLocaleString() : "-"}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Action",
      enableSorting: false,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => openFramework(row.original)}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-gray-300 rounded-lg text-gray-600 hover:text-primary-600 hover:border-primary-300 transition-colors"
        >
          <EyeIcon className="w-3.5 h-3.5" />
          View
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheckIcon className="w-6 h-6" style={{ color: "var(--app-primary-color, #26256b)" }} />
            Compliance
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">DSPM-relevant frameworks for your account</p>
        </div>
      </div>

      {!fwLoading && frameworks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <DocumentTextIcon className="w-14 h-14 mb-3 opacity-40" />
          <p className="text-lg font-medium">No frameworks enabled yet</p>
          <p className="text-sm mt-1">Contact your Platform Admin to have a framework assigned</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
          <DataTable
            columns={frameworkColumns}
            data={frameworks}
            getRowId={(fw) => String(fw.id)}
            loading={fwLoading}
            emptyMessage="No frameworks enabled yet"
            enableColumnVisibility={false}
            enableColumnResizing
            enableColumnFilters
            maxBodyHeight="65vh"
          />
        </div>
      )}
    </div>
  );
};

export default Compliance;
